import React, { type ReactNode } from 'react';

import { cn } from '@change/ds-utils';

import { useParallaxWrapper } from './hooks/useParallaxWrapper/useParallaxWrapper';

export type ParallaxWrapperProps = {
	children: ReactNode;
	strength?: number;
	maxShift?: number;
	mobileStrength?: number;
	mobileMaxShift?: number;
	className?: string;
};

/**
 * A reusable parallax wrapper that applies scroll-based transform effects to its children.
 * The effect respects user accessibility preferences and provides different settings for mobile devices.
 *
 * @param children - The content to apply parallax effect to
 * @param strength - Parallax intensity multiplier for desktop devices (default: 2.5, higher = more movement)
 * @param maxShift - Maximum transform distance in pixels for desktop (default: 400, caps movement range)
 * @param mobileStrength - Parallax intensity multiplier for mobile devices (default: 1.2, typically lower than desktop)
 * @param mobileMaxShift - Maximum transform distance in pixels for mobile (default: 150, typically lower than desktop)
 * @param className - Additional CSS classes to apply to the wrapper element
 *
 * @example
 * ```tsx
 * // Basic usage with default settings
 * <ParallaxWrapper className="h-80">
 *   <img src="hero.jpg" alt="Hero image" />
 * </ParallaxWrapper>
 * ```
 *
 * @example
 * ```tsx
 * // Custom parallax intensity
 * <ParallaxWrapper
 *   strength={4}
 *   maxShift={600}
 *   mobileStrength={1.5}
 *   mobileMaxShift={200}
 *   className="h-100 rounded-lg overflow-hidden"
 * >
 *   <div className="bg-linear-to-r from-blue-500 to-purple-600">
 *     <h1>Dramatic parallax effect</h1>
 *   </div>
 * </ParallaxWrapper>
 * ```
 */
export function ParallaxWrapper({
	children,
	strength = 2.5,
	maxShift = 400,
	mobileStrength = 2.4,
	mobileMaxShift = 390,
	className = '',
}: ParallaxWrapperProps): React.JSX.Element {
	const { wrapperRef } = useParallaxWrapper({
		strength,
		maxShift,
		mobileStrength,
		mobileMaxShift,
	});

	return (
		<div
			ref={wrapperRef}
			className={cn(
				'will-change-transform',
				'[&:not([data-scrolling])]:transition-transform [&:not([data-scrolling])]:duration-500 [&:not([data-scrolling])]:ease-out',
				'motion-reduce:transform-none motion-reduce:transition-none motion-reduce:will-change-auto',
				className,
			)}
			aria-hidden="true"
		>
			{children}
		</div>
	);
}
